import { FC, useState } from 'react';
import { Switch } from '@headlessui/react';

import axiosInstance from 'apis/axiosInstance';
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal';
import { renderToast } from 'components/Toast/Toast';
import { GENERIC_ERROR_MESSAGE } from 'utils/constants';

interface ReportingApiToggleProps {
  isEnabled: boolean;
  onUpdated: () => Promise<unknown>;
}

const ReportingApiToggle: FC<ReportingApiToggleProps> = ({
  isEnabled,
  onUpdated,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const updateSetting = async (isReportingApiEnabled: boolean) => {
    setIsSaving(true);
    try {
      await axiosInstance.patch('/clinics/reporting-api-setting', {
        isReportingApiEnabled,
      });
      await onUpdated();
      renderToast({
        message: `Reporting API turned ${isReportingApiEnabled ? 'on' : 'off'}`,
        type: 'success',
      });
    } catch (error) {
      renderToast({ message: GENERIC_ERROR_MESSAGE, type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (value: boolean) => {
    if (value) {
      updateSetting(true);
      return;
    }
    setIsConfirmModalOpen(true);
  };

  return (
    <div className="bg-white shadow-primary rounded-[1rem] mt-3 px-3 py-2.4 flex justify-between items-center">
      <h4 className="text-16 leading-[2.4rem] font-bold">
        Enable Reporting API
      </h4>
      <div className="flex gap-x-1.6 items-center">
        <Switch
          aria-label="Enable Reporting API"
          onChange={handleChange}
          checked={isEnabled}
          disabled={isSaving}
          className={`${
            isEnabled ? 'bg-magenta' : 'bg-[#78788029]'
          } relative inline-flex h-3.1 w-5.1 items-center rounded-full`}
        >
          <span
            className={`${
              isEnabled ? 'translate-x-[2.3rem]' : 'translate-x-[2px]'
            } inline-block h-2.5 w-2.5 transform rounded-full transition bg-white`}
          />
        </Switch>
        <span className="uppercase">{isEnabled ? 'On' : 'Off'}</span>
      </div>
      {isConfirmModalOpen && (
        <ConfirmationModal
          title="Turn off Reporting API?"
          description="Existing API keys will stop returning this practice's data until the Reporting API is turned back on."
          submitBtnTitle="Turn Off"
          onClose={() => setIsConfirmModalOpen(false)}
          onSubmit={async () => {
            await updateSetting(false);
            setIsConfirmModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ReportingApiToggle;
